import React, { useState, useEffect } from 'react';
import { DonationEmailData, EmailTemplateService } from '../templates/email/emailTemplates';

const EmailPreview: React.FC = () => {
  const [emailHtml, setEmailHtml] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Sample data for preview
  const sampleData: Partial<DonationEmailData> = {
    donor_name: 'John Smith',
    donor_email: 'john.smith@example.com',
    donation_type: 'One-Time',
    donation_amount: 150,
    donation_date: 'January 15, 2025',
    transaction_id: 'TXN-1234567890-ABC',
    is_monthly: false,
    website_url: 'https://goodbyemalaria.com'
  };

  useEffect(() => {
    const loadEmailTemplate = async () => {
      try {
        setLoading(true);
        
        // Generate complete email data
        const emailData = EmailTemplateService.generateDonationConfirmationEmail(sampleData);
        
        // Load the HTML template content (we'll embed it directly for preview)
        const templateHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Thank You for Your Donation - Goodbye Malaria</title>
    <style>
        /* Reset styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            color: #333333;
            background-color: #f8f9fa;
        }
        
        .email-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .header {
            padding: 0;
            text-align: center;
        }
        
        .header img {
            width: 100%;
            height: auto;
            display: block;
        }
        
        .content {
            padding: 40px 30px;
            text-align: left;
        }
        
        .content p {
            margin-bottom: 20px;
            line-height: 1.6;
            color: #333333;
            text-align: left;
        }
        
        .footer {
            background-color: #212326;
            color: white;
            padding: 30px;
            text-align: center;
        }
        
        .footer p {
            margin-bottom: 10px;
            font-size: 14px;
            line-height: 1.5;
        }
        
        .footer a {
            color: #31d3ae;
            text-decoration: none;
        }
        
        
        /* Mobile responsive */
        @media only screen and (max-width: 600px) {
            .email-container {
                width: 100% !important;
            }
            
            .content, .footer {
                padding: 20px !important;
            }
            
            .header img {
                width: 100% !important;
                height: auto !important;
            }
        }
    </style>
</head>
<body>
    <div class="email-container">
        <!-- Header -->
        <div class="header">
            <img src="/images/banner.png" alt="Goodbye Malaria" />
        </div>
        
        <!-- Main Content -->
        <div class="content">
            <p>Hello {{donor_name}},</p>
            
            <p>From all of us at Goodbye Malaria—thank you.</p>
            
            <p>Your donation isn't just a number. It's homes sprayed. Rapid diagnostic testing. Boots on the ground. Lives saved.</p>
            
            <p>They will never know you... but they will feel the effects of the contribution you make. And there is surely no greater feeling than knowing you have diminished a suffering, safeguarded a childhood, or saved a life.</p>
            
            <p>Because of you, we're one step closer to eliminating malaria for good. And that's no small thing.</p>
            
            <p>If you would like an acknowledgment of funds for this donation and/or a Section 18A certificate, please don't hesitate to let us know by replying to this email.</p>
            
            <p>Together, we can say goodbye to malaria—for good.</p>
            
            <p><strong>For if not us? If not now, when?</strong></p>
            
            <p>With deep gratitude,<br>
            <strong>The Goodbye Malaria Team</strong></p>
        </div>
        
        <!-- Footer -->
        <div class="footer">
            <p><strong>Goodbye Malaria</strong></p>
            <p>We are a registered Public Benefit Organisation and can issue donors with Section 18A Tax Certificates.</p>
            <p>For tax certificates or any questions, please contact us at <a href="mailto:info@goodbyemalaria.com">info@goodbyemalaria.com</a></p>
            
            
            <p>&copy; 2025 Goodbye Malaria. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`;
        
        // Replace template variables
        const processedHtml = EmailTemplateService.replaceTemplateVariables(templateHtml, emailData);
        
        setEmailHtml(processedHtml);
        setLoading(false);
      } catch (err) {
        setError('Failed to load email template');
        setLoading(false);
        console.error('Error loading email template:', err);
      }
    };

    loadEmailTemplate();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading Email Preview...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <h2>Error: {error}</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', marginBottom: '20px' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '10px' }}>Email Template Preview</h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          This is how the donation confirmation email will look to recipients
        </p>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h3>Sample Data Used:</h3>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li><strong>Donor Name:</strong> {sampleData.donor_name}</li>
            <li><strong>Email:</strong> {sampleData.donor_email}</li>
            <li><strong>Amount:</strong> R{sampleData.donation_amount}</li>
            <li><strong>Type:</strong> {sampleData.donation_type}</li>
            <li><strong>Date:</strong> {sampleData.donation_date}</li>
          </ul>
        </div>
      </div>
      
      <div 
        style={{ 
          maxWidth: '600px', 
          margin: '0 auto',
          border: '2px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
        dangerouslySetInnerHTML={{ __html: emailHtml }}
      />
      
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: '#31d3ae',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Back to Site
        </button>
      </div>
    </div>
  );
};

export default EmailPreview;
